import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../component/Pagination";
import { toast } from "react-toastify";
import Select from "../component/SelectSearch"
import {
    fetchSubCategories,
    createSubCategory,
    updateSubCategory,
    deleteSubCategory,
} from "../../../slice/subCategory/subCategorySlice";

import { fetchCategoryDropdown } from "../../../slice/category/categorySlice";

export default function ListSubCategory() {
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const limit = 10;
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const { subCategories = [], pagination = {}, loading } = useSelector(
        (state) => state.subCategory
    );

    const { dropdownCategories = [] } = useSelector((state) => state.category);

    const [showAdd, setShowAdd] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});

    const [form, setForm] = useState({
        name: "",
        categoryId: ""
    });

    const [editId, setEditId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    // API Base URL for images
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    useEffect(() => {
        dispatch(fetchCategoryDropdown());
    }, [dispatch]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (page !== 1) {
                setPage(1);
            } else {
                dispatch(fetchSubCategories({ page: 1, limit, search: searchTerm }));
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Group subcategories by parent category
    const groupedData = dropdownCategories.map((category, index) => ({
        id: category._id,
        name: category.name,
        image: category.image,
        serialNo: index + 1,
        subCategories: subCategories.filter(sub => sub.category?._id === category._id)
    })).filter(group => group.subCategories.length > 0);

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    // Handle image file selection with preview
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Get image URL helper
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        return `${API_URL}/uploads/subcategories/${imagePath}`;
    };

    const getCategoryImageUrl = (imagePath) => {
        if (!imagePath) return null;
        return `${API_URL}/uploads/categories/${imagePath}`;
    };

    /* ================= ADD ================= */
    const handleAdd = () => {
        if (!form.categoryId || !form.name.trim()) {
            toast.error("All fields are required");
            return;
        }

        const formData = new FormData();
        formData.append("name", form.name);
        formData.append("categoryId", form.categoryId);
        
        if (imageFile) {
            formData.append("image", imageFile);
        }

        dispatch(createSubCategory(formData))
            .unwrap()
            .then(() => {
                toast.success("Sub-category added");
                setShowAdd(false);
                setForm({ name: "", categoryId: "" });
                setImageFile(null);
                setImagePreview(null);
                dispatch(fetchSubCategories({ page, limit, search: searchTerm }));
            })
            .catch((err) => {
                toast.error(err || "Failed to add sub-category");
            });
    };

    /* ================= EDIT ================= */
    const handleUpdate = () => {
        if (!form.categoryId || !form.name.trim()) {
            toast.error("All fields are required");
            return;
        }

        const formData = new FormData();
        formData.append("name", form.name);
        formData.append("categoryId", form.categoryId);
        if (imageFile) {
            formData.append("image", imageFile);
        }

        dispatch(updateSubCategory({ id: editId, formData }))
            .unwrap()
            .then(() => {
                toast.success("Sub-category updated");
                setShowEdit(false);
                setEditId(null);
                setForm({ name: "", categoryId: "" });
                setImageFile(null);
                setImagePreview(null);
                dispatch(fetchSubCategories({ page, limit, search: searchTerm }));
            })
            .catch((err) => {
                toast.error(err || "Failed to update sub-category");
            });
    };

    /* ================= DELETE ================= */
    const confirmDelete = () => {
        dispatch(deleteSubCategory(deleteId))
            .unwrap()
            .then(() => {
                toast.success("Sub-category deleted");
                setShowDelete(false);
                dispatch(fetchSubCategories({ page, limit, search: searchTerm }));
            });
    };

    const categoryOptions = dropdownCategories.map((c) => ({
        value: c._id,
        label: c.name
    }));

    return (
        <div className="card mt-3">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Sub-Category Management</h4>
                <button className="btn cusBTNPop btn-sm" onClick={() => setShowAdd(true)}>
                    + Add Sub-Category
                </button>
            </div>

            {/* Search Filter */}
            <div className="p-3 border-bottom">
                <div style={{ maxWidth: '250px' }}>
                    <div className="input-group">
                        <span className="input-group-text bg-white">
                            <iconify-icon icon="solar:magnifer-linear" />
                        </span>
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Search Sub-Category"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* TABLE */}
            <div className="table-responsive">
                <table className="nested-table">
                    <thead>
                        <tr>
                          
                            <th style={{ width: '100px' }}>SI</th>
                            <th style={{ width: '80px' }}>Image</th>
                            <th>Sub-Category Name</th>
                            <th>Belongs to Category</th>
                            <th style={{ width: '120px' }}>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                        ) : groupedData.length ? (
                            groupedData.map((group) => (
                                <React.Fragment key={group.id}>
                                    {/* Parent Category Row */}
                                    <tr className="category-row">
                                       
                                        <td>
                                            <span 
                                                className="toggle-icon"
                                                onClick={() => toggleCategory(group.id)}
                                                style={{ cursor: 'pointer', marginRight: '8px' }}
                                            >
                                                {expandedCategories[group.id] ? '▼' : '▶'}
                                            </span>
                                            {group.serialNo}
                                        </td>
                                        <td>
                                            {/* <img 
                                                src={getCategoryImageUrl(group.image)}
                                                
                                                alt={group.name}
                                                style={{ 
                                                    width: '50px', 
                                                    height: '50px', 
                                                    objectFit: 'cover',
                                                    borderRadius: '4px'
                                                }}
                                               
                                            /> */}
                                        </td>
                                        <td>
                                            <strong>{group.name}</strong>
                                        </td>
                                        <td>{group.name}</td>
                                        <td></td>
                                    </tr>

                                    {/* Sub-Category Rows */}
                                    {expandedCategories[group.id] && group.subCategories.map((sub, subIndex) => (
                                        <tr key={sub._id} className="subcategory-row">
                                            <td></td>
                                            <td className="ps-4">
                                                {group.serialNo}.{subIndex + 1}
                                            </td>
                                            <td className="ps-4">
                                                <img 
                                                  
                                                    alt={sub.name}

                                                    src={`${process.env.REACT_APP_API_URL}/uploads/subcategories/${sub.image}`}
                                                    style={{ 
                                                        width: '50px', 
                                                        height: '50px', 
                                                        objectFit: 'cover',
                                                        borderRadius: '4px'
                                                    }}
                                                
                                                />
                                            </td>
                                            <td className="ps-4">
                                                {sub.name}
                                            </td>
                                            <td>{group.name}</td>
                                            <td className="d-flex gap-2">
                                                <button
                                                    className="btn btn-soft-success btn-sm me-2"
                                                    onClick={() => {
                                                        setEditId(sub._id);
                                                        setForm({
                                                            name: sub.name,
                                                            categoryId: sub.category._id
                                                        });
                                                        setImagePreview(getImageUrl(sub.image));
                                                        setShowEdit(true);
                                                    }}
                                                    title="Edit"
                                                >
                                                    <iconify-icon icon="solar:pen-2-broken" />
                                                </button>

                                                <button
                                                    className="btn btn-soft-danger btn-sm"
                                                    onClick={() => {
                                                        setDeleteId(sub._id);
                                                        setShowDelete(true);
                                                    }}
                                                    title="Delete"
                                                >
                                                    <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr><td colSpan="6" className="text-center py-4">
                                {searchTerm ? 'No results found' : 'No sub-categories'}
                            </td></tr>
                        )}
                    </tbody>
                </table>

                <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={setPage}
                />
            </div>

            {/* ================= ADD MODAL ================= */}
            {showAdd && (
                <div className="modal-overlay" onClick={() => setShowAdd(false)}>
                    <div className="cusmodal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5>Add Sub-Category</h5>
                            <button
                                className="btn-close"
                                onClick={() => {
                                    setShowAdd(false);
                                    setImagePreview(null);
                                    setImageFile(null);
                                }}
                            />
                        </div>

                        <div className="modal-body">
                            <label className="form-label">Parent Category</label>
                            <Select
                                options={categoryOptions}
                                placeholder="Select parent category"
                                value={categoryOptions.find(
                                    (opt) => opt.value === form.categoryId
                                )}
                                onChange={(option) =>
                                    setForm({ ...form, categoryId: option.value })
                                }
                            />

                            <label className="form-label mt-3">Sub-Category Name</label>
                            <input
                                className="form-control"
                                placeholder="Enter sub-category name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                            />

                            <label className="form-label mt-3">Sub-Category Image</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                            
                            {/* Image Preview */}
                            {imagePreview && (
                                <div className="mt-3">
                                    <img 
                                        src={imagePreview} 
                                        alt="Preview" 
                                        style={{ 
                                            width: '100px', 
                                            height: '100px', 
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            border: '2px solid #ddd'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="cusmodal-footer">
                            <button
                                className="btn cusClose"
                                onClick={() => {
                                    setShowAdd(false);
                                    setImagePreview(null);
                                    setImageFile(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn cusBTNPop"
                                onClick={handleAdd}
                            >
                                Add Sub-Category
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {showEdit && (
                <div className="modal-overlay" onClick={() => setShowEdit(false)}>
                    <div className="cusmodal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5>Edit Sub-Category</h5>
                            <button
                                className="btn-close"
                                onClick={() => {
                                    setShowEdit(false);
                                    setImagePreview(null);
                                    setImageFile(null);
                                }}
                            />
                        </div>

                        <div className="modal-body">
                            <label className="form-label">Parent Category</label>
                            <Select
                                options={categoryOptions}
                                placeholder="Select parent category"
                                value={categoryOptions.find(
                                    (opt) => opt.value === form.categoryId
                                )}
                                onChange={(option) =>
                                    setForm({ ...form, categoryId: option.value })
                                }
                            />

                            <label className="form-label mt-3">Sub-Category Name</label>
                            <input
                                className="form-control"
                                placeholder="Enter sub-category name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                            />

                            <label className="form-label mt-3">Change Image (optional)</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                            
                            {/* Current/Preview Image */}
                            {imagePreview && (
                                <div className="mt-3">
                                    <p className="text-muted mb-2">
                                        {imageFile ? 'New Image:' : 'Current Image:'}
                                    </p>
                                    <img 
                                        src={imagePreview} 
                                        alt="Preview" 
                                        style={{ 
                                            width: '100px', 
                                            height: '100px', 
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            border: '2px solid #ddd'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="cusmodal-footer">
                            <button
                                className="btn cusClose"
                                onClick={() => {
                                    setShowEdit(false);
                                    setImagePreview(null);
                                    setImageFile(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn cusBTNPop"
                                onClick={handleUpdate}
                            >
                                Update Sub-Category
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE MODAL */}
            {showDelete && (
                <div className="modal-overlay" onClick={() => setShowDelete(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5>Delete Sub-Category</h5>
                            <button
                                className="btn-close"
                                onClick={() => setShowDelete(false)}
                            />
                        </div>

                        <div className="modal-body text-center">
                            <p>Are you sure you want to delete this sub-category?</p>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn cusClose"
                                onClick={() => setShowDelete(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn cuscancel"
                                onClick={confirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}